// =============================================================================
// BP-05-03: Calendar Frontend - React Query Hooks
// =============================================================================

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { calendarApiClient } from '../api';
import type {
  Appointment,
  AppointmentFormData,
  AppointmentUpdateData,
  AppointmentFilters,
  CalendarQueryParams,
  RecurrenceEditScope,
  RecurrenceDeleteScope,
  CalendarExportOptions,
  AppointmentSummary,
} from '../types';

// -----------------------------------------------------------------------------
// Query Keys
// -----------------------------------------------------------------------------

export const calendarKeys = {
  all: ['calendar'] as const,
  appointments: () => [...calendarKeys.all, 'appointments'] as const,
  appointmentsList: (familyId: string, filters?: AppointmentFilters) =>
    [...calendarKeys.appointments(), 'list', familyId, filters] as const,
  appointmentDetail: (id: string) => [...calendarKeys.appointments(), 'detail', id] as const,
  calendar: () => [...calendarKeys.all, 'views'] as const,
  calendarDay: (familyId: string, date: string, childId?: string) =>
    [...calendarKeys.calendar(), 'day', familyId, date, childId] as const,
  calendarWeek: (familyId: string, startDate: string, childId?: string) =>
    [...calendarKeys.calendar(), 'week', familyId, startDate, childId] as const,
  calendarMonth: (familyId: string, year: number, month: number, childId?: string) =>
    [...calendarKeys.calendar(), 'month', familyId, year, month, childId] as const,
  calendarRange: (familyId: string, params: CalendarQueryParams) =>
    [...calendarKeys.calendar(), 'range', familyId, params] as const,
  agenda: (familyId: string, params?: { childId?: string; startDate?: string }) =>
    [...calendarKeys.calendar(), 'agenda', familyId, params] as const,
  conflicts: (familyId: string, startsAt: string, endsAt: string) =>
    [...calendarKeys.all, 'conflicts', familyId, startsAt, endsAt] as const,
};

// -----------------------------------------------------------------------------
// Appointment Queries
// -----------------------------------------------------------------------------

export function useAppointments(
  familyId: string,
  filters?: AppointmentFilters,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.appointmentsList(familyId, filters),
    queryFn: () =>
      calendarApiClient.appointments.list(familyId, {
        childId: filters?.childId,
        appointmentType: filters?.appointmentType,
        status: filters?.status,
        startDate: filters?.startDate,
        endDate: filters?.endDate,
      }),
    enabled: options?.enabled !== false && !!familyId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useAppointment(appointmentId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: calendarKeys.appointmentDetail(appointmentId),
    queryFn: () => calendarApiClient.appointments.get(appointmentId),
    enabled: options?.enabled !== false && !!appointmentId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// -----------------------------------------------------------------------------
// Calendar View Queries
// -----------------------------------------------------------------------------

export function useCalendarDay(
  familyId: string,
  date: string,
  childId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.calendarDay(familyId, date, childId),
    queryFn: () => calendarApiClient.calendar.getDay(familyId, date, childId),
    enabled: options?.enabled !== false && !!familyId && !!date,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCalendarWeek(
  familyId: string,
  startDate: string,
  childId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.calendarWeek(familyId, startDate, childId),
    queryFn: () => calendarApiClient.calendar.getWeek(familyId, startDate, childId),
    enabled: options?.enabled !== false && !!familyId && !!startDate,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCalendarMonth(
  familyId: string,
  year: number,
  month: number,
  childId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.calendarMonth(familyId, year, month, childId),
    queryFn: () => calendarApiClient.calendar.getMonth(familyId, year, month, childId),
    enabled: options?.enabled !== false && !!familyId && !!year && !!month,
    staleTime: 1000 * 60 * 5,
  });
}

export function useCalendarRange(
  familyId: string,
  params: CalendarQueryParams,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.calendarRange(familyId, params),
    queryFn: () => calendarApiClient.calendar.getRange(familyId, params),
    enabled: options?.enabled !== false && !!familyId && !!params.start && !!params.end,
    staleTime: 1000 * 60 * 5,
  });
}

export function useAgenda(
  familyId: string,
  params?: { childId?: string; startDate?: string },
  options?: { enabled?: boolean }
) {
  return useInfiniteQuery({
    queryKey: calendarKeys.agenda(familyId, params),
    queryFn: ({ pageParam }) =>
      calendarApiClient.calendar.getAgenda(familyId, {
        cursor: pageParam,
        childId: params?.childId,
        startDate: params?.startDate,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: options?.enabled !== false && !!familyId,
    staleTime: 1000 * 60 * 5,
  });
}

// -----------------------------------------------------------------------------
// Conflict Check
// -----------------------------------------------------------------------------

export function useConflictCheck(
  familyId: string,
  startsAt: string,
  endsAt: string,
  excludeAppointmentId?: string,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: calendarKeys.conflicts(familyId, startsAt, endsAt),
    queryFn: () =>
      calendarApiClient.appointments.checkConflicts(
        familyId,
        startsAt,
        endsAt,
        excludeAppointmentId
      ),
    enabled: options?.enabled !== false && !!familyId && !!startsAt && !!endsAt,
    staleTime: 0, // Always refetch
  });
}

// -----------------------------------------------------------------------------
// Appointment Mutations
// -----------------------------------------------------------------------------

export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ familyId, data }: { familyId: string; data: AppointmentFormData }) =>
      calendarApiClient.appointments.create(familyId, data),
    onSuccess: (_, { familyId }) => {
      // Invalidate all calendar queries for this family
      queryClient.invalidateQueries({ queryKey: [...calendarKeys.all, familyId] });
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      data,
      recurrenceScope,
    }: {
      appointmentId: string;
      data: AppointmentUpdateData;
      recurrenceScope?: RecurrenceEditScope;
    }) => calendarApiClient.appointments.update(appointmentId, data, recurrenceScope),
    onSuccess: (updatedAppointment) => {
      // Update the cache directly
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      recurrenceScope,
    }: {
      appointmentId: string;
      recurrenceScope?: RecurrenceDeleteScope;
    }) => calendarApiClient.appointments.delete(appointmentId, recurrenceScope),
    onSuccess: (_, { appointmentId }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: calendarKeys.appointmentDetail(appointmentId) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

// -----------------------------------------------------------------------------
// Appointment Action Mutations
// -----------------------------------------------------------------------------

export function useConfirmAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appointmentId: string) => calendarApiClient.actions.confirm(appointmentId),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

export function useCancelAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appointmentId, reason }: { appointmentId: string; reason?: string }) =>
      calendarApiClient.actions.cancel(appointmentId, reason),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

export function useCompleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      notes,
      followUpNeeded,
      followUpNotes,
    }: {
      appointmentId: string;
      notes?: string;
      followUpNeeded?: boolean;
      followUpNotes?: string;
    }) => calendarApiClient.actions.complete(appointmentId, notes, followUpNeeded, followUpNotes),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

export function useRescheduleAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      startsAt,
      endsAt,
      recurrenceScope,
    }: {
      appointmentId: string;
      startsAt: string;
      endsAt: string;
      recurrenceScope?: RecurrenceEditScope;
    }) => calendarApiClient.actions.reschedule(appointmentId, startsAt, endsAt, recurrenceScope),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
      queryClient.invalidateQueries({ queryKey: calendarKeys.appointments() });
      queryClient.invalidateQueries({ queryKey: calendarKeys.calendar() });
    },
  });
}

// -----------------------------------------------------------------------------
// Checklist & Questions Mutations
// -----------------------------------------------------------------------------

export function useUpdateChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      itemId,
      completed,
    }: {
      appointmentId: string;
      itemId: string;
      completed: boolean;
    }) => calendarApiClient.extras.updateChecklistItem(appointmentId, itemId, completed),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
    },
  });
}

export function useAddChecklistItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appointmentId, item }: { appointmentId: string; item: string }) =>
      calendarApiClient.extras.addChecklistItem(appointmentId, item),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
    },
  });
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      questionId,
      answer,
    }: {
      appointmentId: string;
      questionId: string;
      answer: string;
    }) => calendarApiClient.extras.updateQuestion(appointmentId, questionId, answer),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
    },
  });
}

export function useAddQuestion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appointmentId, question }: { appointmentId: string; question: string }) =>
      calendarApiClient.extras.addQuestion(appointmentId, question),
    onSuccess: (updatedAppointment) => {
      queryClient.setQueryData(
        calendarKeys.appointmentDetail(updatedAppointment.id),
        updatedAppointment
      );
    },
  });
}

// -----------------------------------------------------------------------------
// Attendee Mutations
// -----------------------------------------------------------------------------

export function useAddAttendee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appointmentId,
      attendee,
    }: {
      appointmentId: string;
      attendee: { email: string; name: string; role?: string };
    }) => calendarApiClient.attendees.add(appointmentId, attendee),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentDetail(appointmentId),
      });
    },
  });
}

export function useRemoveAttendee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appointmentId, attendeeId }: { appointmentId: string; attendeeId: string }) =>
      calendarApiClient.attendees.remove(appointmentId, attendeeId),
    onSuccess: (_, { appointmentId }) => {
      queryClient.invalidateQueries({
        queryKey: calendarKeys.appointmentDetail(appointmentId),
      });
    },
  });
}

export function useNotifyAttendees() {
  return useMutation({
    mutationFn: (appointmentId: string) => calendarApiClient.attendees.notify(appointmentId),
  });
}

// -----------------------------------------------------------------------------
// Export Hooks
// -----------------------------------------------------------------------------

export function useExportCalendar() {
  return useMutation({
    mutationFn: ({ familyId, options }: { familyId: string; options: CalendarExportOptions }) => {
      if (options.format === 'ical') {
        return calendarApiClient.export.exportIcal(familyId, options);
      }
      return calendarApiClient.export.exportCsv(familyId, options);
    },
    onSuccess: (blob, { options }) => {
      // Download the file
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calendar-export.${options.format === 'ical' ? 'ics' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
  });
}

// -----------------------------------------------------------------------------
// Utility Hooks
// -----------------------------------------------------------------------------

/**
 * Hook to convert appointments to calendar events for react-big-calendar
 */
export function useCalendarEvents(
  appointments: AppointmentSummary[] | undefined
): import('../types').CalendarEvent[] {
  if (!appointments) return [];

  return appointments.map((apt) => ({
    id: apt.id,
    title: apt.title,
    start: new Date(apt.startsAt),
    end: new Date(apt.endsAt),
    allDay: apt.isAllDay,
    resource: apt,
  }));
}
